import React from 'react';
import PatchLevelBadge from './PatchLevelBadge';
import ExternalLink from './ExternalLink';
import { ISSUE_URLS } from './ZestConstants';

// Base configuration class
class IssueTableConfig {
    getBaseUrl(environment) {
        throw new Error('getBaseUrl must be implemented');
    }

    getDefaultVisibleContent() {
        throw new Error('getDefaultVisibleContent must be implemented');
    }

    getColumnDefinitions() {
        throw new Error('getColumnDefinitions must be implemented');
    }

    getVisibleContentOptions() {
        throw new Error('getVisibleContentOptions must be implemented');
    }

    getFilterableFields(issue) {
        throw new Error('getFilterableFields must be implemented');
    }

    getTypeName() {
        throw new Error('getTypeName must be implemented');
    }
}

// Jira-specific configuration
class JiraIssueTableConfig extends IssueTableConfig {
    getBaseUrl() {
        const hostname = window.location.hostname;
        const currentUrl = window.location.href;

        const isBeta = hostname.startsWith('beta.');
        const isLocal = /localhost:\d+/.test(currentUrl);
        const isNotProd = isBeta || isLocal;
        
        return isNotProd ? ISSUE_URLS.JIRA_STAGING : ISSUE_URLS.JIRA_PROD;
    }

    getDefaultVisibleContent() {
        return ["id", "zest_id", "component", "summary", "patch_level"];
    }

    getColumnDefinitions() {
        return [
            {
                id: "id",
                header: "ID",
                cell: (item) => {
                    try {
                        if (item.error) {
                            return '--';
                        }
                        return (
                            <ExternalLink
                                base={this.getBaseUrl(item.environment)}
                                value={item.id}
                            />
                        );
                    } catch (err) {
                        console.log(err);
                        return '--';
                    }
                }
            },
            {
                id: "zest_id",
                header: "Zest ID",
                cell: (item) => item.zest_id ? item.zest_id : '--'
            },
            {
                id: "component",
                header: "Component",
                cell: (item) => item.component ? item.component : '--'
            },
            {
                id: "summary",
                header: "Summary",
                cell: (item) => item.summary ? item.summary : '--'
            },
            {
                id: "patch_level",
                header: "Patch Level",
                cell: (item) => <PatchLevelBadge patch_level={item.patch_level} />
            }
        ];
    }

    getVisibleContentOptions() {
        return [
            {
                label: "Fields",
                options: [
                    {
                        id: "id",
                        label: "Issue ID",
                        editable: false
                    },
                    { id: "zest_id", label: "Zest ID" },
                    { id: "component", label: "Component" },
                    { id: "summary", label: "Summary" },
                    { id: "patch_level", label: "Patch Level" }
                ]
            }
        ];
    }

    getFilterableFields(issue) {
        return [
            issue.id ? issue.id.toLowerCase() : '',
            issue.zest_id ? issue.zest_id.toLowerCase() : '',
            issue.summary ? issue.summary.toLowerCase() : '',
            issue.component ? issue.component.toLowerCase() : '',
            issue.patch_level ? issue.patch_level.toLowerCase() : ''
        ];
    }

    getTypeName() {
        return 'Jira(s)';
    }
}

// Shepherd-specific configuration
class ShepherdIssueTableConfig extends IssueTableConfig {
    getBaseUrl() {
        // Check if server exists and doesn't contain 'beta'
        // Default to beta if no environment provided
        const hostname = window.location.hostname;
        const currentUrl = window.location.href;

        const isBeta = hostname.startsWith('beta.');
        const isLocal = /localhost:\d+/.test(currentUrl);
        const isNotProd = isBeta || isLocal;
        
        return isNotProd ? ISSUE_URLS.SHEPHERD_PROD : ISSUE_URLS.SHEPHERD_BETA;
    }

    getDefaultVisibleContent() {
        return ["id", "zest_id", "bindle_id", "summary", "patch_level"];
    }

    getColumnDefinitions() {
        return [
            {
                id: "id",
                header: "ID",
                cell: (item) => {
                    try {
                        if (item.error) {
                            return '--';
                        }
                        return (
                            <ExternalLink
                                base={this.getBaseUrl(item.environment)}
                                value={item.id}
                            />
                        );
                    } catch (err) {
                        console.log(err);
                        return '--';
                    }
                }
            },
            {
                id: "zest_id",
                header: "Zest ID",
                cell: (item) => item.zest_id ? item.zest_id : '--'
            },
            {
                id: "bindle_id",
                header: "Bindle ID",
                cell: (item) => item.bindle_id ? item.bindle_id : '--'
            },
            {
                id: "summary",
                header: "Summary",
                cell: (item) => item.summary ? item.summary : '--'
            },
            {
                id: "patch_level",
                header: "Patch Level",
                cell: (item) => <PatchLevelBadge patch_level={item.patch_level} />
            }
        ];
    }

    getVisibleContentOptions() {
        return [
            {
                label: "Fields",
                options: [
                    {
                        id: "id",
                        label: "Issue ID",
                        editable: false
                    },
                    { id: "zest_id", label: "Zest ID" },
                    { id: "bindle_id", label: "Bindle ID" },
                    { id: "summary", label: "Summary" },
                    { id: "patch_level", label: "Patch Level" }
                ]
            }
        ];
    }

    getFilterableFields(issue) {
        return [
            issue.id ? issue.id.toLowerCase() : '',
            issue.zest_id ? issue.zest_id.toLowerCase() : '',
            issue.bindle_id ? issue.bindle_id.toLowerCase() : '',
            issue.summary ? issue.summary.toLowerCase() : '',
            issue.patch_level ? issue.patch_level.toLowerCase() : ''
        ];
    }

    getTypeName() {
        return 'Shepherd Issue(s)';
    }
}

// Factory function to get the appropriate config
export function getIssueTableConfig(issueType) {
    switch (issueType) {
        case 'shepherd':
            return new ShepherdIssueTableConfig();
        case 'jira':
        default:
            return new JiraIssueTableConfig();
    }
}
